import { useState, useEffect, useCallback } from 'react';
import { PageHeader, Badge } from '@/components/ui/PageHeader';
import { SelectField } from '@/components/ui/FormFields';
import { AuthButton } from '@/components/ui/AuthButton';
import { useAuth } from '@/context/AuthContext';
import {
  listTemplates,
  getTemplate,
  type BoardTemplate,
  type LayoutMode,
  LAYOUT_OPTIONS,
} from '@/api/nostalgia';

export default function TemplatesList() {
  const { isAuthenticated } = useAuth();

  const [templates, setTemplates] = useState<BoardTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Filters
  const [layoutFilter, setLayoutFilter] = useState('');

  const fetchTemplates = useCallback(async () => {
    if (!isAuthenticated) return;

    setIsLoading(true);
    try {
      const response = await listTemplates({
        layoutMode: layoutFilter as LayoutMode || undefined,
      });
      setTemplates(response.data);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, layoutFilter]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <h1 className="text-2xl font-bold">Board Templates</h1>
            <AuthButton />
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-yellow-800">Please login to view templates.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <PageHeader
            title="Board Templates"
            subtitle="Pre-designed layouts for nostalgia boards"
            breadcrumbs={[
              { label: 'Nostalgia', href: '/lab/nostalgia' },
              { label: 'Templates' },
            ]}
          />
          <AuthButton />
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg border p-4 mb-6">
          <div className="flex flex-wrap items-end gap-4">
            <div className="w-48">
              <SelectField
                label="Layout Mode"
                value={layoutFilter}
                onChange={(e) => setLayoutFilter(e.target.value)}
                options={LAYOUT_OPTIONS}
                placeholder="All Layouts"
              />
            </div>
          </div>
        </div>

        {/* Templates Grid */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : templates.length === 0 ? (
          <div className="bg-white rounded-lg border p-12 text-center text-gray-500">
            No templates found. Templates are seeded via database.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {templates.map((template) => (
              <div
                key={template.id}
                className="bg-white rounded-lg border overflow-hidden hover:shadow-md transition-shadow"
              >
                {template.previewImageUrl ? (
                  <img
                    src={template.previewImageUrl}
                    alt={template.name}
                    className="w-full h-40 object-cover"
                  />
                ) : (
                  <div className="w-full h-40 bg-gray-100 flex items-center justify-center text-gray-400">
                    No preview
                  </div>
                )}
                <div className="p-4">
                  <h3 className="font-semibold text-gray-900">{template.name}</h3>
                  {template.description && (
                    <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                      {template.description}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-2 mt-3">
                    <Badge variant="blue">{template.layoutMode}</Badge>
                    <Badge variant="gray">{template.slotCount} slots</Badge>
                    {template.defaultEra && (
                      <Badge variant="purple">{template.defaultEra.replace('era_', '')}</Badge>
                    )}
                    {template.category && (
                      <Badge variant="green">{template.category}</Badge>
                    )}
                  </div>
                  <div className="text-xs text-gray-400 mt-2">
                    Slug: {template.slug}
                  </div>
                  <TemplateProbe slug={template.slug} />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Info */}
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h3 className="font-medium text-blue-900 mb-2">About Templates</h3>
          <p className="text-sm text-blue-800">
            Templates define the layout structure for boards. Each template has a specific slot
            configuration that determines where items can be placed. When creating a board,
            users can select a template to start with a pre-designed layout.
          </p>
        </div>
      </div>
    </div>
  );
}

// The single-template read, fetched BY SLUG rather than id.
//
// That is the whole point of the endpoint and the reason it is worth a button:
// a template is linked to from outside the app ("start a board from this one"),
// and a link that carries a uuid is a link nobody can read or hand-write. The
// list already has the data — this proves the addressable route resolves.

function TemplateProbe({ slug }: { slug: string }) {
  const [result, setResult] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  return (
    <div className="mt-2">
      <button
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          setResult(null);
          try {
            const res = await getTemplate(slug);
            const t = res.data.template;
            setResult(`resolved: ${t.name} · ${t.slotCount} slots · ${t.layoutMode}`);
          } catch (e) {
            setResult(e instanceof Error ? e.message : 'did not resolve');
          } finally {
            setBusy(false);
          }
        }}
        className="text-xs text-blue-700 underline disabled:opacity-50"
      >
        fetch by slug
      </button>
      {result && <p className="text-xs text-gray-600 mt-1">{result}</p>}
    </div>
  );
}
